import React from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

import { hasValidAdminUnitRouteId } from "../../../pages/admin/uebersicht/uebersichtHelpers";
import { money, pct, selectWorstUnit } from "./performanceSelectors";

/**
 * The unit the server named as weakest — resolved by id, never by array position.
 *
 * `worst_unit_id` is the contract's answer and this card looks it up in `items`.
 * The legacy panel took `ranked_units[-1]` instead, which always shows SOMETHING
 * — including when the server's own answer and the array disagree, at which
 * point the screen is naming a unit the server did not choose and nothing says
 * so. When the id is missing or names a row the list does not carry, this card
 * says it has no data.
 *
 * NOT AN ALARM. The old card wore a red top rule, red text and a "Live" badge.
 * A negative margin is a real result about a real unit — often a unit mid-lease
 * or newly onboarded — not a fault, and dressing it as one trains a reader to
 * treat the whole tab as an error display. The card carries the same surface as
 * its neighbours and lets the figure speak; "schwächste" is already the ranking's
 * own word for the last place.
 */

const CARD = "rounded-xl border border-border bg-card p-4 shadow-sm";

function CardShell({ subtitle, children }) {
  return (
    <section
      data-testid="performance-weakest-unit-card"
      aria-labelledby="performance-weakest-heading"
      className={CARD}
    >
      <h2
        id="performance-weakest-heading"
        className="m-0 text-[13px] font-semibold text-card-foreground"
      >
        Schwächste Einheit
      </h2>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{subtitle}</p>
      {children}
    </section>
  );
}

export default function PerformanceWeakestUnitCard({ ranking, stand = "", loading = false }) {
  const subtitle = `Letzter Rang nach Objektmarge${stand}`;

  if (loading) {
    return (
      <CardShell subtitle={subtitle}>
        <div className="mt-3 space-y-2" aria-busy="true">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-10 w-full" />
        </div>
      </CardShell>
    );
  }

  const worst = selectWorstUnit(ranking);

  if (!worst) {
    return (
      <CardShell subtitle={subtitle}>
        <p className="mt-4 text-[12px] text-muted-foreground">Keine Daten</p>
      </CardShell>
    );
  }

  const label = worst.unit_label || "Unbekannte Einheit";

  return (
    <CardShell subtitle={subtitle}>
      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <p className="m-0 min-w-0 break-words text-[14px] font-medium text-card-foreground">
          {label}
        </p>
        <p className="m-0 shrink-0 text-[22px] font-semibold tabular-nums text-card-foreground">
          {pct(worst.operating_margin_pct)}
        </p>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div>
          <dt className="text-[10px] uppercase tracking-[0.4px] text-muted-foreground">Umsatz</dt>
          <dd className="m-0 mt-0.5 text-[12px] tabular-nums text-card-foreground">
            {money(worst.expected_recurring_revenue_chf)}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-[0.4px] text-muted-foreground">
            Objektergebnis
          </dt>
          <dd className="m-0 mt-0.5 text-[12px] tabular-nums text-card-foreground">
            {money(worst.operating_result_chf)}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-[0.4px] text-muted-foreground">
            Auslastung
          </dt>
          <dd className="m-0 mt-0.5 text-[12px] tabular-nums text-card-foreground">
            {pct(worst.monthly_occupancy_pct)}
          </dd>
        </div>
      </dl>

      {hasValidAdminUnitRouteId({ id: worst.unit_id }) ? (
        <div className="mt-3 flex justify-end">
          <Link
            to={`/admin/units/${encodeURIComponent(String(worst.unit_id))}`}
            data-testid={`performance-weakest-unit-${worst.unit_id}`}
            className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Zur Einheit →
          </Link>
        </div>
      ) : null}
    </CardShell>
  );
}
